/**
 * CaptureFlowDemoPage - Demo/Prototyp für visuellen Flow-Builder
 * DEMO ONLY - Keine Auswirkung auf echte Erfassung!
 */

import React, { useState, useEffect } from 'react';
import {
  ArrowRight, GripVertical, ChevronUp, ChevronDown, Plus, Trash2, Settings,
  Layers, Users, Scale, GitBranch, Package, AlertTriangle, X, Pencil, Check, Route, Upload,
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

const MOCK_LAUNDRY_CLASSES = [
  { id: 'lc1', name: 'Hotelwäsche' },
  { id: 'lc2', name: 'Gesundheitswäsche' },
  { id: 'lc3', name: 'Heimbewohnerwäsche' },
];

const MOCK_ARTICLE_GROUPS = [
  { id: 'ag1', name: 'Wischbezüge' },
  { id: 'ag2', name: 'Spannlaken' },
  { id: 'ag3', name: 'Bademäntel' },
];

type StepType = 'line' | 'subLine' | 'laundryClass' | 'articleGroup' | 'customer' | 'weight';

interface StepConfig {
  required?: boolean;
  allowedIds?: string[];
  conditionalMode?: 'always' | 'conditional';
  conditionalLaundryClassIds?: string[];
}

interface FlowStepData {
  id: string;
  type: StepType;
  label: string;
  isFixed: boolean;
  config: StepConfig;
}

interface FlowStep extends FlowStepData {
  icon: React.ElementType;
}

interface DemoLine {
  id: string;
  name: string;
  flow: FlowStepData[];
  createdAt: string;
}

interface DemoState {
  lines: DemoLine[];
  version: number;
}

const DEMO_STORAGE_KEY = 'wash-ops-capture-flow-demo-v2';

const STEP_ICONS: Record<StepType, React.ElementType> = {
  line: GitBranch, subLine: Route, laundryClass: Layers,
  articleGroup: Package, customer: Users, weight: Scale,
};

const AVAILABLE_STEPS: { type: StepType; label: string }[] = [
  { type: 'subLine', label: 'Substrecke' },
  { type: 'laundryClass', label: 'Wäscheklasse' },
  { type: 'articleGroup', label: 'Artikelgruppe' },
];

const toRuntimeStep = (step: FlowStepData): FlowStep => ({
  ...step,
  icon: STEP_ICONS[step.type] || Settings,
});

const createDefaultFlow = (): FlowStepData[] => [
  { id: 'step-line', type: 'line', label: 'Strecke', isFixed: true, config: {} },
  { id: 'step-customer', type: 'customer', label: 'Kunde', isFixed: true, config: {} },
  { id: 'step-weight', type: 'weight', label: 'Gewicht', isFixed: true, config: {} },
];

const createInitialDemoState = (): DemoState => ({
  version: 1,
  lines: [
    {
      id: 'demo-line-1', name: 'Handgelegt (Demo)', createdAt: new Date().toISOString(),
      flow: [
        { id: 'step-line', type: 'line', label: 'Strecke', isFixed: true, config: {} },
        { id: 'step-laundry', type: 'laundryClass', label: 'Wäscheklasse', isFixed: false, config: { required: true, allowedIds: [] } },
        { id: 'step-customer', type: 'customer', label: 'Kunde', isFixed: true, config: {} },
        { id: 'step-weight', type: 'weight', label: 'Gewicht', isFixed: true, config: {} },
      ],
    },
    {
      id: 'demo-line-2', name: 'Mangeln (Demo)', createdAt: new Date().toISOString(),
      flow: [
        { id: 'step-line', type: 'line', label: 'Strecke', isFixed: true, config: {} },
        { id: 'step-subline', type: 'subLine', label: 'Substrecke', isFixed: false, config: {} },
        { id: 'step-customer', type: 'customer', label: 'Kunde', isFixed: true, config: {} },
        { id: 'step-weight', type: 'weight', label: 'Gewicht', isFixed: true, config: {} },
      ],
    },
  ],
});

export default function CaptureFlowDemoPage() {
  const [demoState, setDemoState] = useState<DemoState | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [editingLineName, setEditingLineName] = useState<string | null>(null);
  const [tempLineName, setTempLineName] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(DEMO_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as DemoState;
        setDemoState(parsed);
        if (parsed.lines.length > 0) setSelectedLineId(parsed.lines[0].id);
      } else {
        const initial = createInitialDemoState();
        setDemoState(initial);
        setSelectedLineId(initial.lines[0]?.id || null);
      }
    } catch {
      const initial = createInitialDemoState();
      setDemoState(initial);
      setSelectedLineId(initial.lines[0]?.id || null);
    }
  }, []);

  useEffect(() => {
    if (demoState) {
      try { localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demoState)); } catch { /* ignore */ }
    }
  }, [demoState]);

  const currentLine = demoState?.lines.find((l) => l.id === selectedLineId) || null;
  const flowSteps: FlowStep[] = currentLine?.flow.map(toRuntimeStep) || [];
  const selectedStep = flowSteps.find((s) => s.id === selectedStepId);

  const addLine = () => {
    if (!demoState) return;
    const newLine: DemoLine = {
      id: 'demo-line-' + Date.now(), name: 'Neue Strecke',
      createdAt: new Date().toISOString(), flow: createDefaultFlow(),
    };
    setDemoState({ ...demoState, lines: [...demoState.lines, newLine] });
    setSelectedLineId(newLine.id);
    setSelectedStepId(null);
    setEditingLineName(newLine.id);
    setTempLineName(newLine.name);
  };

  const deleteLine = (lineId: string) => {
    if (!demoState || demoState.lines.length <= 1) return;
    const newLines = demoState.lines.filter((l) => l.id !== lineId);
    setDemoState({ ...demoState, lines: newLines });
    if (selectedLineId === lineId) {
      setSelectedLineId(newLines[0]?.id || null);
      setSelectedStepId(null);
    }
  };

  const startEditLineName = (lineId: string) => {
    const line = demoState?.lines.find((l) => l.id === lineId);
    if (line) { setEditingLineName(lineId); setTempLineName(line.name); }
  };

  const saveLineName = () => {
    if (!demoState || !editingLineName || !tempLineName.trim()) { setEditingLineName(null); return; }
    setDemoState({
      ...demoState,
      lines: demoState.lines.map((l) => l.id === editingLineName ? { ...l, name: tempLineName.trim() } : l),
    });
    setEditingLineName(null);
  };

  const updateLineFlow = (newFlow: FlowStepData[]) => {
    if (!demoState || !selectedLineId) return;
    setDemoState({
      ...demoState,
      lines: demoState.lines.map((l) => l.id === selectedLineId ? { ...l, flow: newFlow } : l),
    });
  };

  const moveStep = (index: number, direction: 'up' | 'down') => {
    const step = flowSteps[index];
    if (step.isFixed) return;
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= flowSteps.length || flowSteps[newIndex].isFixed) return;
    const newFlow = [...flowSteps];
    [newFlow[index], newFlow[newIndex]] = [newFlow[newIndex], newFlow[index]];
    updateLineFlow(newFlow.map(({ icon, ...rest }) => rest));
  };

  const addStep = (type: StepType) => {
    const template = AVAILABLE_STEPS.find((s) => s.type === type);
    if (!template || (type !== 'subLine' && flowSteps.some((s) => s.type === type))) return;
    const newStep: FlowStepData = {
      id: 'step-' + type + '-' + Date.now(), type, label: template.label, isFixed: false,
      config: type === 'laundryClass' ? { required: true, allowedIds: [] }
        : type === 'articleGroup' ? { conditionalMode: 'always', conditionalLaundryClassIds: [] } : {},
    };
    const customerIndex = flowSteps.findIndex((s) => s.type === 'customer');
    const insertIndex = customerIndex > 0 ? customerIndex : flowSteps.length - 2;
    const newFlow = [...flowSteps];
    newFlow.splice(insertIndex, 0, toRuntimeStep(newStep));
    updateLineFlow(newFlow.map(({ icon, ...rest }) => rest));
    setSelectedStepId(newStep.id);
  };

  const removeStep = (stepId: string) => {
    const step = flowSteps.find((s) => s.id === stepId);
    if (!step || step.isFixed) return;
    updateLineFlow(flowSteps.filter((s) => s.id !== stepId).map(({ icon, ...rest }) => rest));
    if (selectedStepId === stepId) setSelectedStepId(null);
  };

  const updateStepConfig = (stepId: string, config: Partial<StepConfig>) => {
    const newFlow = flowSteps.map((s) => s.id === stepId ? { ...s, config: { ...s.config, ...config } } : s);
    updateLineFlow(newFlow.map(({ icon, ...rest }) => rest));
  };

  const handleDragStart = (index: number) => { if (!flowSteps[index].isFixed) setDraggedIndex(index); };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index || flowSteps[index].isFixed) { setDraggedIndex(null); return; }
    const newFlow = [...flowSteps];
    const [removed] = newFlow.splice(draggedIndex, 1);
    newFlow.splice(index, 0, removed);
    updateLineFlow(newFlow.map(({ icon, ...rest }) => rest));
    setDraggedIndex(null);
  };

  if (!demoState) return <AdminLayout><div className="p-8 text-gray-500">Lade Demo-Daten...</div></AdminLayout>;

  return (
    <AdminLayout>
    <div className="bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Settings className="w-6 h-6" />Erfassungs-Flow (Demo)
            </h1>
            <p className="text-sm text-gray-500 mt-1">Strecken und deren Erfassungs-Flows modellieren</p>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <span className="text-sm text-amber-800">Demo / Konzept - keine Auswirkung auf echte Erfassung</span>
          </div>
        </div>
      </div>

      <div className="flex">
        <div className="w-72 bg-white border-r min-h-[calc(100vh-80px)] flex flex-col">
          <div className="p-4 border-b">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Strecken-Flows</h2>
            <button onClick={addLine} className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" />Neue Strecke
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {demoState.lines.map((line) => (
              <div key={line.id} onClick={() => { setSelectedLineId(line.id); setSelectedStepId(null); }}
                className={'group relative p-3 rounded-lg mb-2 cursor-pointer transition-all ' + (selectedLineId === line.id ? 'bg-blue-50 border-2 border-blue-500' : 'bg-gray-50 border-2 border-transparent hover:border-gray-300')}>
                <div className="flex items-center gap-2">
                  <GitBranch className={'w-4 h-4 ' + (selectedLineId === line.id ? 'text-blue-600' : 'text-gray-500')} />
                  {editingLineName === line.id ? (
                    <div className="flex-1 flex items-center gap-1">
                      <input type="text" value={tempLineName} onChange={(e) => setTempLineName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveLineName(); if (e.key === 'Escape') setEditingLineName(null); }}
                        autoFocus className="flex-1 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <button onClick={saveLineName} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <span className={'flex-1 text-sm font-medium ' + (selectedLineId === line.id ? 'text-blue-900' : 'text-gray-700')}>{line.name}</span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-400">{line.flow.length} Schritte</div>
                {editingLineName !== line.id && (
                  <div className="absolute top-2 right-2 hidden group-hover:flex gap-1">
                    <button onClick={(e) => { e.stopPropagation(); startEditLineName(line.id); }} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                    {demoState.lines.length > 1 && <button onClick={(e) => { e.stopPropagation(); deleteLine(line.id); }} className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="p-4 border-t">
            <button disabled className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed">
              <Upload className="w-4 h-4" />Als echte Strecke übernehmen
            </button>
            <p className="text-xs text-gray-400 mt-2 text-center">(Platzhalter)</p>
          </div>
        </div>

        <div className="flex-1 p-6 flex flex-col">
          {currentLine ? (
            <>
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-gray-800">{currentLine.name}</h2>
                <p className="text-sm text-gray-500">Erfassungs-Flow bearbeiten</p>
              </div>
              <div className="mb-6 p-4 bg-white rounded-lg border">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Schritte hinzufügen:</h3>
                <div className="flex gap-2 flex-wrap">
                  {AVAILABLE_STEPS.map((step) => {
                    const alreadyInFlow = step.type !== 'subLine' && flowSteps.some((s) => s.type === step.type);
                    const Icon = STEP_ICONS[step.type];
                    return (
                      <button key={step.type} onClick={() => addStep(step.type)} disabled={alreadyInFlow}
                        className={'flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ' + (alreadyInFlow ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-700')}>
                        <Plus className="w-4 h-4" /><Icon className="w-4 h-4" /><span className="text-sm">{step.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">Erfassungs-Flow</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  {flowSteps.map((step, index) => {
                    const Icon = step.icon;
                    const isSelected = selectedStepId === step.id;
                    const isDragging = draggedIndex === index;
                    return (
                      <React.Fragment key={step.id}>
                        <div draggable={!step.isFixed} onDragStart={() => handleDragStart(index)} onDragOver={handleDragOver} onDrop={() => handleDrop(index)}
                          onClick={() => !step.isFixed && setSelectedStepId(step.id)}
                          className={'relative flex flex-col items-center p-4 rounded-xl border-2 transition-all min-w-[120px] ' + (isDragging ? 'opacity-50 ' : '') + (step.isFixed ? 'bg-gray-100 border-gray-300 cursor-default' : isSelected ? 'bg-blue-50 border-blue-500 cursor-pointer shadow-md' : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer')}>
                          {!step.isFixed && (
                            <div className="absolute -top-2 -right-2 flex gap-0.5">
                              <button onClick={(e) => { e.stopPropagation(); moveStep(index, 'up'); }} className="p-1 bg-white border rounded shadow-sm hover:bg-gray-50"><ChevronUp className="w-3 h-3 -rotate-90" /></button>
                              <button onClick={(e) => { e.stopPropagation(); moveStep(index, 'down'); }} className="p-1 bg-white border rounded shadow-sm hover:bg-gray-50"><ChevronDown className="w-3 h-3 -rotate-90" /></button>
                              <button onClick={(e) => { e.stopPropagation(); removeStep(step.id); }} className="p-1 bg-white border rounded shadow-sm hover:bg-red-50"><X className="w-3 h-3 text-red-500" /></button>
                            </div>
                          )}
                          {!step.isFixed && <GripVertical className="w-4 h-4 text-gray-300 absolute top-1 left-1" />}
                          <div className={'w-10 h-10 rounded-full flex items-center justify-center mb-2 ' + (step.isFixed ? 'bg-gray-200 text-gray-500' : isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600')}><Icon className="w-5 h-5" /></div>
                          <span className={'text-sm font-medium ' + (step.isFixed ? 'text-gray-500' : 'text-gray-800')}>{step.label}</span>
                          {step.isFixed && <span className="text-xs text-gray-400 mt-1">(fest)</span>}
                          {!step.isFixed && step.config.required === false && <span className="text-xs text-amber-600 mt-1">optional</span>}
                          {!step.isFixed && step.config.conditionalMode === 'conditional' && <span className="text-xs text-purple-600 mt-1">bedingt</span>}
                        </div>
                        {index < flowSteps.length - 1 && <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">Wähle eine Strecke aus der Liste links</div>
          )}
        </div>

        <div className="w-80 bg-white border-l p-4 min-h-[calc(100vh-80px)]">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Schritt-Einstellungen</h2>
          {!selectedStep ? (
            <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-lg">Wähle einen Schritt im Flow aus.</div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2">
                  <selectedStep.icon className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">{selectedStep.label}</span>
                </div>
              </div>
              {selectedStep.type === 'laundryClass' && (
                <>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={selectedStep.config.required !== false} onChange={(e) => updateStepConfig(selectedStep.id, { required: e.target.checked })} className="w-4 h-4 text-blue-600 rounded" />
                    <span className="text-sm font-medium text-gray-700">Pflichtfeld</span>
                  </label>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Erlaubte Wäscheklassen</label>
                    <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                      {MOCK_LAUNDRY_CLASSES.map((lc) => (
                        <label key={lc.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={selectedStep.config.allowedIds?.includes(lc.id) ?? false}
                            onChange={(e) => { const c = selectedStep.config.allowedIds || []; updateStepConfig(selectedStep.id, { allowedIds: e.target.checked ? [...c, lc.id] : c.filter((id) => id !== lc.id) }); }}
                            className="w-4 h-4 text-blue-600 rounded" />
                          <span className="text-sm text-gray-700">{lc.name}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Keine Auswahl = alle erlaubt</p>
                  </div>
                </>
              )}
              {selectedStep.type === 'articleGroup' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Modus</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="conditionalMode" checked={selectedStep.config.conditionalMode !== 'conditional'} onChange={() => updateStepConfig(selectedStep.id, { conditionalMode: 'always' })} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-700">Immer erforderlich</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="conditionalMode" checked={selectedStep.config.conditionalMode === 'conditional'} onChange={() => updateStepConfig(selectedStep.id, { conditionalMode: 'conditional' })} className="w-4 h-4 text-blue-600" />
                        <span className="text-sm text-gray-700">Nur bei bestimmten Wäscheklassen</span>
                      </label>
                    </div>
                  </div>
                  {selectedStep.config.conditionalMode === 'conditional' && (
                    <div className="space-y-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                      {MOCK_LAUNDRY_CLASSES.map((lc) => (
                        <label key={lc.id} className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={selectedStep.config.conditionalLaundryClassIds?.includes(lc.id) ?? false}
                            onChange={(e) => { const c = selectedStep.config.conditionalLaundryClassIds || []; updateStepConfig(selectedStep.id, { conditionalLaundryClassIds: e.target.checked ? [...c, lc.id] : c.filter((id) => id !== lc.id) }); }}
                            className="w-4 h-4 text-purple-600 rounded" />
                          <span className="text-sm text-gray-700">{lc.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  <div className="space-y-1 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                    <p className="font-medium mb-1">Verfügbare Artikelgruppen:</p>
                    {MOCK_ARTICLE_GROUPS.map((ag) => <div key={ag.id}>{ag.name}</div>)}
                  </div>
                </>
              )}
              {selectedStep.type === 'subLine' && (
                <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">
                  Substrecken werden dynamisch geladen.
                </div>
              )}
              <button onClick={() => removeStep(selectedStep.id)} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg border border-red-200 hover:bg-red-100">
                <Trash2 className="w-4 h-4" />Schritt entfernen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    </AdminLayout>
  );
}
